import * as React from 'react';
import { useInView } from 'react-intersection-observer';
import { FormattedMessage } from 'react-intl';

import { GetItemDataByIdQuery } from '@notino/shared/definitions/types';

import { GTM_WISHLIST_LIST_NAME } from '@containers/WishList/constants';
import messages from '@containers/WishList/messages';
import { useWishlistStore } from '@containers/WishList/store';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';
import { useAddToCartCloudTracking } from '@hooks/useAddToCardCloudTracking';

import AnimatedProductList from '../AnimatedProductList';
import {
  trackWishlistPageView,
  useTrackToCartAsync,
  useTrackToCartFailedAsync,
} from '../WishlistPager/tracking';

import { FiltersSection } from './FiltersSection';
import { Container, ProductsCount } from './styled';
import { useWishlistViewItemListTracking } from './useWishlistViewItemListTracking';
import { WishlistHeader } from './WishlistHeader';

export const WishlistPage = () => {
  const { spa_redirect_wishlist_to_pd } = useFeatureFlags();
  const products = useWishlistStore((state) => state.products);
  const hasMore = useWishlistStore((state) => state.hasMore);
  const productIds = useWishlistStore((state) => state.productIds);
  const isShared = useWishlistStore((state) => state.isSharedWishlist);
  const totalProductCount = useWishlistStore(
    (state) => state.totalProductCount
  );
  const loading = useWishlistStore((state) => state.isLoading);
  const sortBy = useWishlistStore((state) => state.selectedSortBy);
  const { setProductWishlisted, fetchProducts } = useWishlistStore(
    (state) => state.actions
  );

  const trackAddToCartAsync = useTrackToCartAsync(
    productIds,
    GTM_WISHLIST_LIST_NAME
  );
  const addToCardCloudTracking = useAddToCartCloudTracking();
  const trackAddToCartFailedAsync = useTrackToCartFailedAsync(
    productIds,
    GTM_WISHLIST_LIST_NAME
  );

  const handleProductAdded = async (
    product: GetItemDataByIdQuery['vpProductById']
  ) => {
    await trackAddToCartAsync(product);
    addToCardCloudTracking({ quantity: 1, productCode: product.productCode });
  };

  const trackItemView = useWishlistViewItemListTracking();

  return (
    <Container>
      <WishlistHeader isShared={isShared} />

      <FiltersSection />

      <ProductsCount>
        <FormattedMessage {...messages.productsCount} />: {totalProductCount}
      </ProductsCount>

      <AnimatedProductList
        isLoading={loading}
        isSharedWishlist={isShared}
        disableAnimation={loading}
        products={products}
        showAddToCartModal={false}
        canSpaRedirect={spa_redirect_wishlist_to_pd}
        productItemProps={{
          GTMEventLabel: GTM_WISHLIST_LIST_NAME,
          sendGTM: true,
          noPriceIfUnavailable: true,
          showBuyButton: true,
          onProductAdded: handleProductAdded,
          onProductAddFailed: trackAddToCartFailedAsync,
          onClosingCartModal: trackWishlistPageView,
          setProductWishlisted,
          inSectionOfType: 'wishlist',
          wishlistActionLocation: 'wishlist',
        }}
        onProductInView={trackItemView}
      />

      {hasMore && !loading && (
        <LoadMoreSensor
          loadMore={() =>
            fetchProducts({
              offset: productIds.length,
              sortBy,
            })
          }
        />
      )}
    </Container>
  );
};

const LoadMoreSensor = ({ loadMore }: { loadMore: () => void }) => {
  const [ref] = useInView({
    rootMargin: '0px 0px 100px 0px',
    onChange: (inView) => {
      if (inView) {
        loadMore();
      }
    },
  });

  return <div ref={ref} />;
};
